/**
 * Aikido adapter. REAL: `getSecurityFrom` reads the open findings the API pulls from Aikido.
 * Colour and status come from the central threshold config, never here.
 */
import i18n from '@app/i18n';
import { ProductSecurity } from '@data/utils/types';
import { SecurityIssueRow, SecurityMetric, SeveritySplit } from './types';
import { securityStatus } from './thresholds';

const total = (s: SeveritySplit): number => s.critical + s.high + s.medium + s.low;

/**
 * The scan types Aikido runs against a code repository, in the order the breakdown lists them. Every
 * one is shown even at zero, so a client can see what was checked and came back clean — a list of only
 * the failures cannot distinguish "clean" from "never scanned".
 *
 * Verified as active in the workspace: each of these produces findings on some repository, so a zero is
 * a real result rather than a scanner that never ran. Cloud and surface monitoring are excluded because
 * they attach to cloud and domain assets, not to a repository.
 */
const CODE_REPO_CHECKS = ['open_source', 'sast', 'leaked_secret', 'iac', 'malware', 'eol'] as const;

/** Aikido's own `filter_issue_type` values. */
const CHECK_LABELS: Record<string, string> = {
  open_source: 'securityCheckOpenSource',
  leaked_secret: 'securityCheckLeakedSecret',
  sast: 'securityCheckSast',
  iac: 'securityCheckIac',
  cloud: 'securityCheckCloud',
  docker_container: 'securityCheckContainer',
  surface_monitoring: 'securityCheckSurface',
  malware: 'securityCheckMalware',
  eol: 'securityCheckEol',
  license: 'securityCheckLicense',
};

const checkLabel = (type: string): string => {
  const key = CHECK_LABELS[type];
  if (key) return i18n.t(`productDashboard.${key}`);
  return type.replace(/_/g, ' ').replace(/^./, character => character.toUpperCase());
};

/**
 * Every code-repository check in a fixed order, zero-filled, followed by any type Aikido returned that
 * this list does not know — a new scan type appears rather than being silently dropped.
 */
const buildChecks = (found: ProductSecurity['checks']): { name: string; issues: number }[] => {
  const counts = new Map(found.map(check => [check.type, check.issues]));
  const known = CODE_REPO_CHECKS.map(type => ({ name: checkLabel(type), issues: counts.get(type) ?? 0 }));
  const extra = found
    .filter(check => !CODE_REPO_CHECKS.includes(check.type as (typeof CODE_REPO_CHECKS)[number]))
    .map(check => ({ name: checkLabel(check.type), issues: check.issues }));
  return [...known, ...extra];
};

const SEVERITY_LABELS = {
  critical: 'severityCritical',
  high: 'severityHigh',
  medium: 'severityMedium',
  low: 'severityLow',
} as const;

const toRows = (issues: ProductSecurity['issues']): SecurityIssueRow[] =>
  issues.map(issue => ({
    ...issue,
    kind: checkLabel(issue.type),
    severityLabel: i18n.t(`productDashboard.${SEVERITY_LABELS[issue.severity]}`),
  }));

export const aikido = {
  // Every repository archived is a different unknown from scanning never set up, and the card says which.
  noDataKeyFor(data?: ProductSecurity): string {
    return data?.isAllArchived ? 'productDashboard.securityAllArchived' : 'productDashboard.securityNoData';
  },

  /**
   * Real data. Returns null when Aikido does not scan this product, which is NOT the same as a product
   * with nothing open — a zero would read to a client as a clean bill of health.
   */
  getSecurityFrom(data: ProductSecurity): SecurityMetric | null {
    if (!data.isScanned) return null;

    const severities: SeveritySplit = { critical: data.critical, high: data.high, medium: data.medium, low: data.low };
    const openIssues = total(severities);

    return {
      total: openIssues,
      status: securityStatus(severities),
      value: String(openIssues),
      checks: buildChecks(data.checks),
      issues: toRows(data.issues ?? []),
      lastScannedAt: data.lastScannedAt,
    };
  },
};
